// NR · Night hero: deferred footage, the headline streak, the light trails and the headlight.
// The poster is the LCP image; the video only starts once the page has loaded and the browser is idle,
// and never for reduced-motion, data-saver or slow-link visitors. The headlight only ever writes a transform, at most
// once per frame, from passive listeners, so it never blocks scrolling or input. Nothing loops forever: the trails run
// two passes and replay when the hero comes back into view or the light is taken.

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const isMobile = window.matchMedia('(max-width: 749px)');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

function slowConnection() {
  const c = navigator.connection;
  return Boolean(c && (c.saveData || /(^|-)(2g|3g)$/.test(c.effectiveType || '') || (c.downlink > 0 && c.downlink < 2)));
}

function whenIdle(fn) {
  const run = () => ('requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 2500 }) : setTimeout(fn, 600));
  if (document.readyState === 'complete') run();
  else window.addEventListener('load', run, { once: true });
}

function initHero(hero) {
  if (hero.dataset.nrReady) return;
  hero.dataset.nrReady = '1';

  const video = hero.querySelector('.nr-hero__video');
  const pauseBtn = hero.querySelector('[data-nr-hero-pause]');
  let userPaused = false;
  let inView = true;

  // Headline streak: only over the real display face. If it is not in within 1.5 s the streak is skipped
  // (the headline itself is always shown at once).
  const streak = hero.querySelector('.nr-hero__streak');
  if (streak && !reduceMotion.matches && document.fonts?.load) {
    const t0 = performance.now();
    const ready = (late) => hero.classList.add(late ? 'is-type-late' : 'is-type-ready');
    document.fonts.load(`1em ${getComputedStyle(streak).fontFamily}`).then(
      () => ready(performance.now() - t0 > 1500),
      () => ready(true)
    );
  } else if (streak) hero.classList.add('is-type-ready');

  // Light trails: the band ends just above the copy (phones; desktop keeps its own band on the right). Measured in a
  // ResizeObserver callback, after layout, so it never forces one.
  const trails = hero.querySelector('.nr-hero__trails');
  const copy = hero.querySelector('.nr-hero__content');
  if (trails && copy) {
    const band = new ResizeObserver(() => {
      // the copy's first line, in hero coordinates (the copy itself is an offsetParent: it has a scroll-driven transform)
      let top = 0;
      for (let n = copy.firstElementChild; n && n !== hero; n = n.offsetParent) top += n.offsetTop;
      hero.style.setProperty('--nr-trails-bottom', `${Math.max(0, hero.clientHeight - top + 14)}px`);
    });
    band.observe(copy);
    band.observe(hero);
  }
  // ...and two passes per showing: replayed (only once finished) when the hero is back in view or the light is taken
  const trailCount = trails ? trails.children.length : 0;
  let trailsEnded = 0;
  trails?.addEventListener('animationend', () => trailsEnded++);
  const replayTrails = () => {
    if (!trailCount || trailsEnded < trailCount || reduceMotion.matches) return;
    trailsEnded = 0;
    hero.classList.toggle('is-replay');
  };

  const play = () => {
    if (!video?.src || userPaused || !inView) return;
    video.play().catch(() => {});
  };

  // One control pauses everything that moves in the hero: footage, headlight drift, light trails
  pauseBtn?.addEventListener('click', () => {
    userPaused = !userPaused;
    hero.classList.toggle('is-paused', userPaused);
    pauseBtn.querySelector('[data-label-pause]').hidden = userPaused;
    pauseBtn.querySelector('[data-label-play]').hidden = !userPaused;
    if (userPaused) video?.pause();
    else play();
  });

  // Off screen: loops pause (CSS) and the footage stops
  new IntersectionObserver(([entry]) => {
    const back = entry.isIntersecting && !inView;
    inView = entry.isIntersecting;
    hero.classList.toggle('is-offscreen', !inView);
    if (back) replayTrails();
    if (inView) play();
    else video?.pause();
  }).observe(hero);

  if (video && !reduceMotion.matches && !slowConnection()) {
    const src = isMobile.matches ? hero.dataset.srcMobile : hero.dataset.srcDesktop;
    if (src) {
      whenIdle(() => {
        video.src = src;
        video.addEventListener('playing', () => hero.classList.add('is-playing'), { once: true });
        play();
      });
    }
  }

  const beam = hero.querySelector('[data-nr-beam]');
  if (!beam || reduceMotion.matches) return;

  let rect = null;
  let frame = 0;
  let x = 0;
  let y = 0;
  let restTimer = 0;
  const forget = () => {
    rect = null;
  };
  addEventListener('scroll', forget, { passive: true, capture: true });
  addEventListener('resize', forget, { passive: true });

  const place = () => {
    frame = 0;
    beam.style.transform = `translate3d(${x}px, ${y}px, 0)`;
  };
  const aim = (event) => {
    rect ||= hero.getBoundingClientRect();
    x = event.clientX - rect.left;
    y = event.clientY - rect.top;
    if (!frame) frame = requestAnimationFrame(place);
  };
  const steer = () => {
    clearTimeout(restTimer);
    if (!hero.classList.contains('is-steering')) replayTrails();
    hero.classList.add('is-steering', 'has-steered');
  };
  // Back to its resting spot (CSS eases it there); phones pick the drift up again from that spot
  const rest = () => {
    cancelAnimationFrame(frame);
    frame = 0;
    hero.classList.remove('is-steering');
    beam.style.transform = '';
  };

  if (finePointer.matches) {
    // Desktop: the light follows the cursor over the hero
    hero.addEventListener('pointerenter', steer, { passive: true });
    hero.addEventListener('pointermove', aim, { passive: true });
    hero.addEventListener('pointerleave', rest, { passive: true });
    return;
  }

  // Opt in before the gesture: changing touch-action after pointerdown cannot stop a browser scroll.
  const lightBtn = hero.querySelector('[data-nr-hero-light]');
  const toggleLight = (active) => {
    active = active && !reduceMotion.matches;
    hero.classList.toggle('is-light-interactive', active);
    lightBtn?.setAttribute('aria-pressed', String(active));
    if (lightBtn) {
      lightBtn.querySelector('[data-light-start]').hidden = active;
      lightBtn.querySelector('[data-light-done]').hidden = !active;
    }
    if (!active) rest();
  };
  if (lightBtn) {
    lightBtn.hidden = false;
    lightBtn.addEventListener('click', () => toggleLight(!hero.classList.contains('is-light-interactive')));
  }
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') toggleLight(false);
  });
  reduceMotion.addEventListener('change', () => toggleLight(false));
  let touchId = null;
  hero.addEventListener(
    'pointerdown',
    (event) => {
      if (!hero.classList.contains('is-light-interactive') || event.pointerType === 'mouse' || event.target.closest('a, button')) return;
      touchId = event.pointerId;
      hero.setPointerCapture(touchId);
      steer();
      aim(event);
    },
    { passive: true }
  );
  hero.addEventListener(
    'pointermove',
    (event) => {
      if (event.pointerId === touchId) aim(event);
    },
    { passive: true }
  );
  const release = () => {
    touchId = null;
    if (!hero.classList.contains('is-steering')) return;
    clearTimeout(restTimer);
    restTimer = setTimeout(rest, 2200);
  };
  hero.addEventListener('pointerup', release, { passive: true });
  hero.addEventListener('pointercancel', release, { passive: true });
}

document.querySelectorAll('[data-nr-hero]').forEach(initHero);
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-nr-hero]').forEach(initHero);
});
